(function (globalRef) {
  const GLOBAL_KEY = "__fishystuffDiagnosticReportContract";
  const MAX_ISSUES = 20;
  const MAX_DECLARED_ISSUES = 10000;
  const MAX_REPETITIONS = 1000;
  const MAX_DESCRIPTION_LENGTH = 1000;
  const SOURCES = new Set([
    "browser",
    "browser-console",
    "browser-network",
    "browser-resource",
    "map-api",
    "page",
  ]);
  const CATEGORIES = new Set([
    "api",
    "console",
    "fetch",
    "general",
    "resource",
    "runtime",
    "xhr",
  ]);
  const TRIGGERS = new Set(["manual"]);
  const SEVERITIES = new Set(["error", "warning", "info"]);
  const PAGE_AREAS = [
    "map",
    "calculator",
    "fishydex",
    "profile",
    "guides",
    "devlog",
    "community",
  ];

  function string(value) {
    return String(value ?? "").trim();
  }

  function classification(value, allowed) {
    const normalized = string(value).toLowerCase();
    return allowed.has(normalized) ? normalized : "other";
  }

  function pageArea(value) {
    const explicitArea = string(value?.area).toLowerCase();
    if (explicitArea === "site" || PAGE_AREAS.includes(explicitArea)) {
      return explicitArea;
    }
    const path = string(value?.path ?? value).split(/[?#]/, 1)[0];
    for (const area of PAGE_AREAS) {
      if (path === `/${area}` || path.startsWith(`/${area}/`)) {
        return area;
      }
    }
    return "site";
  }

  function reportId() {
    if (typeof globalRef.crypto?.randomUUID === "function") {
      return `health-report-${globalRef.crypto.randomUUID()}`;
    }
    return `health-report-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  function issue(value) {
    return {
      severity: classification(value?.severity, SEVERITIES),
      source: classification(value?.source, SOURCES),
      category: classification(value?.category, CATEGORIES),
      count: Math.min(
        MAX_REPETITIONS,
        Math.max(1, Number.parseInt(value?.count, 10) || 1),
      ),
    };
  }

  function description(value) {
    if (
      typeof value !== "string"
      || value.length > MAX_DESCRIPTION_LENGTH
      || value.trim().length === 0
    ) {
      throw new TypeError(`Diagnostic report description must contain 1-${MAX_DESCRIPTION_LENGTH} text characters.`);
    }
    return value;
  }

  function report(options = {}) {
    const rawIssues = Array.isArray(options.issues) ? options.issues : [];
    const issues = rawIssues.slice(0, MAX_ISSUES).map(issue);
    const declaredIssueCount = Number.parseInt(options.issueCount, 10);
    const issueCount = Number.isFinite(declaredIssueCount)
      ? Math.min(MAX_DECLARED_ISSUES, Math.max(issues.length, declaredIssueCount))
      : Math.min(MAX_DECLARED_ISSUES, rawIssues.length);
    const mode = "manual";
    return {
      schemaVersion: 2,
      createdAt: new Date().toISOString(),
      page: {
        area: pageArea(options.page),
      },
      report: {
        id: reportId(),
        category: "page-health",
        issueCount,
        reportedIssueCount: issues.length,
        mode,
        trigger: classification(options.trigger, TRIGGERS),
        description: description(options.description),
      },
      health: {
        hiddenByUserSetting: options.hiddenByUserSetting === true,
        issues,
      },
    };
  }

  function create(options = {}) {
    return report(options);
  }

  function sanitize(value, _options = {}) {
    const source = value && typeof value === "object" ? value : {};
    return report({
      issues: source.health?.issues,
      issueCount: source.report?.issueCount,
      description: source.report?.description,
      trigger: source.report?.trigger,
      page: source.page,
      hiddenByUserSetting: source.health?.hiddenByUserSetting,
    });
  }

  globalRef[GLOBAL_KEY] = Object.freeze({
    create,
    maxDescriptionLength: MAX_DESCRIPTION_LENGTH,
    sanitize,
  });
})(globalThis);
